// 結果画面の表示文言辞書とフロント算出ヘルパー。
// サーバーは enum / score を返し、日本語文言はここ（フロント辞書）で組む（compatibility_radar 流儀）。
// enum は値として import し、分岐の比較は enum メンバー参照にする（値の改名をコンパイルで検知するため）。
// jest では api/generated 経由の axios(ESM) を各テストが `jest.mock("axios", …)` でスタブする
import {
  InvestmentStyleDiagnosis,
  InvestmentStyleDiagnosisCustomerTypeEnum as CustomerType,
  InvestmentStyleDiagnosisCoreGoalEnum as CoreGoal,
  InvestmentStyleDiagnosisPrimaryPurposeEnum as PrimaryPurpose,
  InvestmentStyleDiagnosisInterestScoreKeyEnum as InterestKey,
  InvestmentStyleDiagnosisSuitedAssetsEnum as Asset,
  InvestmentStyleDiagnosisMeetingQuestionKeyEnum as MeetingKey,
  InvestmentStyleDiagnosisAnxietyScoreKeyEnum as AnxietyKey,
  InvestmentStyleDiagnosisAvoidSalesStyleWarningsEnum as WarningKey,
  InvestmentStyleDiagnosisHoldingPeriodEnum as HoldingKey,
  InvestmentStyleDiagnosisMonthlyOutOfPocketEnum as MonthlyKey,
  InvestmentStyleDiagnosisExplanationStylesEnum as ExplanationKey,
  InvestmentStyleDiagnosisSuccessConditionsEnum as SuccessKey,
} from "@/api/generated";

import LocationCharacter from "@assets/image/investment_style_location.png";
import LongTermCharacter from "@assets/image/investment_style_long_term.png";
import NoneCharacter from "@assets/image/investment_style_none.png";
import ShortTermCharacter from "@assets/image/investment_style_short_term.png";
import YieldCharacter from "@assets/image/investment_style_yield.png";

// 顧客タイプ名（ヘッダー）
export const CUSTOMER_TYPE_LABEL: Record<CustomerType, string> = {
  long_term_hold: "長期保有型",
  location_focused: "立地重視型",
  yield_focused: "利回り重視型",
  short_term_gain: "短期売却益型",
};

// 顧客タイプ別のキャラクター画像。結果画面のヘッダーとホームのミニ表示で同じ絵柄を使う
export const CUSTOMER_TYPE_CHARACTER: Record<CustomerType, string> = {
  long_term_hold: LongTermCharacter,
  location_focused: LocationCharacter,
  yield_focused: YieldCharacter,
  short_term_gain: ShortTermCharacter,
};

// 表紙でタイプ名の下に並べる特徴タグ（3つ固定）。タイプごとの固定文言で、回答から組み立てない。
// タイプを表示できないとき（typed=false）はタイプ行ごと出さないため、ここにフォールバックは持たない
export const CUSTOMER_TYPE_TRAITS: Record<CustomerType, readonly [string, string, string]> = {
  long_term_hold: ["長く持つ", "家賃で積み上げる", "売り急がない"],
  location_focused: ["10年ほど持つ", "駅に近い部屋", "空室が出にくい"],
  yield_focused: ["10年ほど持つ", "利回りを優先", "エリアを広げる"],
  short_term_gain: ["売却益を狙う", "売り時を見極める", "税と費用に注意"],
};

// buildTypeDisplay の入力。customer_type が nil のレコードは実在しうる（API 側 allow_nil）うえ、
// サーバが種別を増やしてクライアント未再生成の間は enum に無い文字列も届きうるため、
// customer_type / core_goal は生成型の enum に限定せず API のレスポンスそのままの形で受ける
type TypeDisplaySource = Omit<InvestmentStyleDiagnosis, "customer_type" | "core_goal"> & {
  customer_type?: string | null;
  core_goal?: string | null;
};

// サーバ由来の文字列で辞書を引くときは自身のキーに限定する。
// "constructor" 等の prototype キーだと lookup が関数を返して truthy 判定を素通りし、
// React の children / img src に関数が渡って描画が壊れるため
const ownLookup = <K extends string, V>(dict: Partial<Record<K, V>>, key: string | null | undefined): V | undefined =>
  key != null && Object.prototype.hasOwnProperty.call(dict, key) ? dict[key as K] : undefined;

// タイプ表示（ラベル・タイプ名見出し・キャラクター画像）の組み立て。
// 結果画面のヘッダーとホームのサマリーカードで同じ判断を使うため、定義元はここに一本化する。
// customer_type が nil / 辞書に無いタイプでも、見出し・画像ともフォールバックを置いて
// 空文字や壊れた画像にならないようにする。
// 辞書に無いタイプでは画像が未診断用（NoneCharacter）に倒れるため、ラベルだけ
// 「あなたのタイプ」の診断済み表現にすると未診断と誤解される。ラベルは診断の有無ではなく
// 「タイプ名を表示できるか」で切り替えて、画像との組み合わせを揃える。
// typed はタイプ名を表示できるかどうか。結果画面（表紙「あなたの投資方針書」）はタイプを出せないときに
// 「タイプ判定なし」とは書かず、タイプの行と特徴タグ（CUSTOMER_TYPE_TRAITS）ごと出さないため、
// フォールバックの見出しはホームのサマリーカード用。ホームは単独表示なので「あなたの投資スタイル」のままで良い
export const buildTypeDisplay = (
  diagnosis: TypeDisplaySource | null
): { typed: boolean; label: string; headline: string; character: string; traits: readonly string[] } => {
  const typeLabel = ownLookup(CUSTOMER_TYPE_LABEL, diagnosis?.customer_type);
  const typeCharacter = ownLookup(CUSTOMER_TYPE_CHARACTER, diagnosis?.customer_type);
  const typeTraits = ownLookup(CUSTOMER_TYPE_TRAITS, diagnosis?.customer_type);
  if (typeLabel && typeCharacter && typeTraits) {
    return {
      typed: true,
      label: "あなたのタイプ",
      headline: typeLabel,
      character: typeCharacter,
      traits: typeTraits,
    };
  }
  // タイプを表示できないときは特徴タグも無し（表紙はタイプ行とタグをまとめて出さない）
  return {
    typed: false,
    label: "あなたの不動産投資タイプ",
    headline: diagnosis ? "あなたの投資スタイル" : "まだ診断されていません",
    character: NoneCharacter,
    traits: [],
  };
};

// coreGoal 「本質は◯◯」に差し込む語
export const CORE_GOAL_LABEL: Record<CoreGoal, string> = {
  retirement_income: "老後収入の土台づくり",
  long_term_asset: "長期資産形成",
  family_protection: "家族に資産を残す備え",
  side_income: "安定した副収入づくり",
  low_effort: "手間を抑えた資産運用",
  yield_improvement: "収益性と改善余地の見極め",
  loss_avoidance: "大きな損失を避けた資産形成",
  short_term_gain: "短期の売却益",
};

// ① 見出し（primaryPurpose 別）。節税は別分岐で生成
const PURPOSE_TITLE: Partial<Record<PrimaryPurpose, string>> = {
  retirement_income: "老後の補助収入 ＋ 長期資産",
  inflation_hedge: "インフレに備える資産分散",
  life_insurance: "万一に備える保険代替（家族に残す）",
  side_income: "キャッシュフロー型の副収入",
  short_term_gain: "短期の売却益（キャピタルゲイン）狙い",
};

export const buildHeadline = (diagnosis: InvestmentStyleDiagnosis): string => {
  if (diagnosis.primary_purpose === PrimaryPurpose.TaxSaving) {
    const essence = ownLookup(CORE_GOAL_LABEL, diagnosis.core_goal);
    return essence ? `入口は節税、本質は${essence}` : "最も重視する目的：節税";
  }
  return ownLookup(PURPOSE_TITLE, diagnosis.primary_purpose) || "";
};

// ⑥ 現時点のスタンス（ヘッダー一言）
const TAX_STANCE_TEMPLATE: Record<CoreGoal, string> = {
  long_term_asset: "節税を入口にしつつ、本質は長期の資産形成を着実に狙うスタンス",
  retirement_income: "節税を入口にしつつ、本質は老後収入の土台づくりを狙うスタンス",
  family_protection: "節税を入口にしつつ、本質は家族に資産を残す備えを重視するスタンス",
  side_income: "節税を入口にしつつ、本質は安定した副収入づくりを狙うスタンス",
  low_effort: "節税を入口にしつつ、手間を抑えた資産運用を重視するスタンス",
  yield_improvement: "節税を入口にしつつ、収益性と改善余地を慎重に見極めるスタンス",
  loss_avoidance: "節税を入口にしつつ、大きな損失を避けた資産形成を重視するスタンス",
  short_term_gain: "節税を入口にしつつ、本質は短期の売却益を狙う（流動性・税・コストに注意）スタンス",
};
const TYPE_STANCE_TEMPLATE: Record<CustomerType, string> = {
  long_term_hold: "長期でじっくり資産形成を狙うスタンス",
  location_focused: "立地の良さを重視し、着実に資産を持ちたいスタンス",
  yield_focused: "手間をかけてでも、利回りと改善余地を狙う行動派スタンス",
  short_term_gain: "短期の売却益を狙う（流動性・税・コストに注意）スタンス",
};

export const buildStance = (diagnosis: InvestmentStyleDiagnosis): string => {
  const strongAnxietyCount = (diagnosis.anxiety_scores ?? []).filter((s) => (s.score ?? 0) >= 100).length;
  if (strongAnxietyCount >= 3) return "不安要素を一つずつ確認し、慎重に進めたいスタンス";
  if (diagnosis.customer_type === CustomerType.ShortTermGain) {
    return "短期の売却益を狙い、流動性・税・コストを慎重に確認するスタンス";
  }
  // 辞書に無い値（サーバが種別を増やしてクライアント未再生成の間など）は undefined になるため、
  // 空文字に倒して呼び出し側の `{stance && …}` で行ごと非表示にする（buildTypeDisplay の救済と対称）
  if (diagnosis.primary_purpose === PrimaryPurpose.TaxSaving && diagnosis.core_goal) {
    return ownLookup(TAX_STANCE_TEMPLATE, diagnosis.core_goal) ?? "";
  }
  return ownLookup(TYPE_STANCE_TEMPLATE, diagnosis.customer_type) ?? "";
};

// ① 関心グラフ（9項目）短縮ラベル
export const INTEREST_LABEL: Record<InterestKey, string> = {
  stable_income: "安定収入",
  long_term_asset: "長期資産形成",
  low_effort: "手間のかからなさ",
  high_yield: "高利回り",
  short_term_gain: "短期の売却益",
  tax_saving: "節税",
  loss_avoidance_safety: "損をしにくい",
  loss_avoidance_low_initial: "初期費用の低さ",
  loss_avoidance_trusted: "相談しながら進める",
};

// ② 投資スタンス
export const HOLDING_RESULT_LABEL: Record<HoldingKey, string> = {
  within_5y: "5年以内の売却も視野",
  about_10y: "10年程度",
  about_20y: "20年以上",
  about_30y: "30年〜ローン完済まで長期",
  after_payoff: "完済後も長期保有",
};
export const MONTHLY_RESULT_LABEL: Record<MonthlyKey, string> = {
  near_zero: "0円に近い",
  within_5k: "5千円以内",
  within_10k: "1万円以内",
  within_30k: "3万円以内",
  within_50k: "5万円以内",
  over_50k: "5万円超も許容",
};

// ③ 物件タイプ（アセット名）
export const ASSET_LABEL: Record<Asset, string> = {
  new_kubun: "新築区分",
  urban_used_kubun: "都心・準都心の中古区分",
  renovated_kubun: "リノベ区分",
  urban_new_ittou: "都心・準都心の新築・築浅一棟",
  urban_old_ittou: "都心・準都心の築古一棟",
  rural_new_ittou: "地方の新築・築浅一棟",
  rural_old_ittou: "地方の築古一棟",
};

// ④ リスク許容度（不安）バー
export const ANXIETY_RESULT_LABEL: Record<AnxietyKey, string> = {
  loan: "ローン負担",
  vacancy: "空室",
  exit: "出口（売却）",
  repair: "修繕費",
  sales_pressure: "営業に流される",
};

// ④ 避けたい提案への警告
export const WARNING_LABEL: Record<WarningKey, string> = {
  overpriced: "高値掴みに注意",
  tax_only: "節税だけを強調する提案（最重要の注意）",
  pushy_immediate: "即決を迫る営業に注意",
  high_management: "管理負担の大きい提案に注意",
  excessive_loan: "過大な借入提案に注意",
};

// ⑤ 面談で聞くと良い質問（Q10 → 質問文）
export const MEETING_QUESTION_TEXT: Record<MeetingKey, string> = {
  monthly_real_cost: "空室のとき、実質負担はいくら？",
  vacancy_cost: "空室が長引く前提の、最大の持ち出しは？",
  interest_rate_rise: "金利1%上昇でCFはマイナスになりませんか？",
  rent_decline: "築年が進んだ後の賃料下落はどの程度？",
  repair_cost: "修繕履歴と、今後の想定修繕費は？",
  sale_price: "想定売却価格と、その根拠は？",
  tax_effect: "節税は何年で切れて、その後の収支は？",
  unfit_case: "この物件が私に合わないケースは？",
  comparison: "他社の同種物件と比べた優位は？",
  loan_consult: "利用できる融資条件と、無理なく返済できる借入額を教えてください。",
  support_detail: "購入後の管理・サポート範囲と、追加でかかる費用を教えてください。",
};

export const MEETING_COMMON_MESSAGE =
  "回答は楽観ケースだけでなく、空室・金利上昇・家賃下落を含む数字で確認してください。";

// ⑤ 説明条件（Q11）
export const EXPLANATION_RESULT_LABEL: Record<ExplanationKey, string> = {
  beginner_friendly: "基礎から丁寧に",
  with_numbers: "具体的な数字で",
  risk_first: "リスクや失敗例も率直に",
  pros_cons: "複数の選択肢を比較",
  vs_other_investments: "他の投資との違いも",
  no_jargon: "専門用語を使わず",
  with_rationale: "根拠まで詳しく",
  concise_points: "重要ポイントを簡潔に",
  own_pace: "自分のペースで",
  proactive_suggestion: "積極的に提案してほしい",
};

export const SUCCESS_CONDITION_LABEL: Record<SuccessKey, string> = {
  retirement_monthly: "老後の生活費を家賃収入で",
  avoid_big_loss: "大きな損をせず長く続ける",
  tax_achieved: "節税ができている",
  debt_free_asset: "完済後も資産が残る",
  able_to_expand: "無理なく買い増せる",
};
